import { useCallback, useEffect, useMemo, useRef, useState } from "react";

function defaultGetItemKey(item) {
  return item?._id || item?.id || item?.slug || JSON.stringify(item);
}

function mergeUniqueItems(oldItems, newItems, getItemKey) {
  const map = new Map();

  for (const item of oldItems) {
    map.set(getItemKey(item), item);
  }

  for (const item of newItems) {
    map.set(getItemKey(item), item);
  }

  return Array.from(map.values());
}

/**
 * Generic infinite scroll hook
 *
 * Expected fetcher response:
 * {
 *   items: [],
 *   hasMore: boolean,
 *   nextPage: number | null
 * }
 *
 * Example fetcher usage:
 * fetcher({ page, signal }) => Promise<{ items, hasMore, nextPage }>
 */
export default function useInfiniteScroll({
  fetcher,
  enabled = true,
  initialPage = 1,
  initialItems = [],
  initialHasMore = true,
  initialNextPage = null,
  root = null,
  rootMargin = "300px",
  threshold = 0,
  getItemKey = defaultGetItemKey,
}) {
  const [items, setItems] = useState(initialItems);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [nextPage, setNextPage] = useState(
    initialNextPage ?? (initialItems?.length ? initialPage + 1 : initialPage),
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isFirstLoadDone, setIsFirstLoadDone] = useState(
    initialItems.length > 0,
  );
  const [error, setError] = useState(null);

  const observerRef = useRef(null);
  const sentinelRef = useRef(null);

  const isFetchingRef = useRef(false);
  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);

  const mountedRef = useRef(false);

  const stableInitialState = useMemo(() => {
    return {
      items: initialItems,
      hasMore: initialHasMore,
      nextPage:
        initialNextPage ??
        (initialItems?.length ? initialPage + 1 : initialPage),
      isFirstLoadDone: initialItems.length > 0,
    };
  }, [initialHasMore, initialItems, initialNextPage, initialPage]);

  const cancelActiveRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    isFetchingRef.current = false;
  }, []);

  const loadMore = useCallback(async () => {
    if (!enabled) return;
    if (!hasMore) return;
    if (isFetchingRef.current) return;

    cancelActiveRequest();

    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    const currentRequestId = ++requestIdRef.current;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetcher({
        page: nextPage,
        signal: controller.signal,
      });

      // Eğer bu response eski request'e aitse ignore et
      if (currentRequestId !== requestIdRef.current) return;

      const newItems = Array.isArray(response?.items) ? response.items : [];
      const newHasMore = Boolean(response?.hasMore);
      const resolvedNextPage =
        response?.nextPage !== undefined && response?.nextPage !== null
          ? response.nextPage
          : nextPage + 1;

      setItems((prev) => mergeUniqueItems(prev, newItems, getItemKey));
      setHasMore(newHasMore);
      setNextPage(resolvedNextPage);
      setIsFirstLoadDone(true);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setError(err);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [enabled, hasMore, nextPage, fetcher, getItemKey, cancelActiveRequest]);

  const reload = useCallback(async () => {
    if (!enabled) return;

    cancelActiveRequest();

    setItems([]);
    setHasMore(true);
    setNextPage(initialPage);
    setError(null);
    setIsFirstLoadDone(false);

    // request id artır ki eski response append edemesin
    requestIdRef.current += 1;
    isFetchingRef.current = false;

    // ilk sayfayı yeniden çek
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const currentRequestId = ++requestIdRef.current;

    try {
      isFetchingRef.current = true;
      setIsLoading(true);

      const response = await fetcher({
        page: initialPage,
        signal: controller.signal,
      });

      if (currentRequestId !== requestIdRef.current) return;

      const newItems = Array.isArray(response?.items) ? response.items : [];
      const newHasMore = Boolean(response?.hasMore);
      const resolvedNextPage =
        response?.nextPage !== undefined && response?.nextPage !== null
          ? response.nextPage
          : initialPage + 1;

      setItems(mergeUniqueItems([], newItems, getItemKey));
      setHasMore(newHasMore);
      setNextPage(resolvedNextPage);
      setIsFirstLoadDone(true);
    } catch (err) {
      if (err?.name !== "AbortError") {
        setError(err);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        isFetchingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [enabled, cancelActiveRequest, fetcher, getItemKey, initialPage]);

  const reset = useCallback(() => {
    cancelActiveRequest();

    requestIdRef.current += 1;

    setItems([]);
    setHasMore(true);
    setNextPage(initialPage);
    setError(null);
    setIsLoading(false);
    setIsFirstLoadDone(false);

    isFetchingRef.current = false;
  }, [cancelActiveRequest, initialPage]);

  const hydrate = useCallback(
    ({
      items: hydratedItems = [],
      hasMore: hydratedHasMore = true,
      nextPage: hydratedNextPage = initialPage + 1,
    }) => {
      cancelActiveRequest();

      requestIdRef.current += 1;

      setItems(hydratedItems);
      setHasMore(hydratedHasMore);
      setNextPage(hydratedNextPage);
      setError(null);
      setIsLoading(false);
      setIsFirstLoadDone(hydratedItems.length > 0);

      isFetchingRef.current = false;
    },
    [cancelActiveRequest, initialPage],
  );

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      cancelActiveRequest();

      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [cancelActiveRequest]);

  // initial auto load
  useEffect(() => {
    if (!enabled) return;
    if (stableInitialState.items.length > 0) return;
    if (isFirstLoadDone) return;

    loadMore();
  }, [enabled, stableInitialState.items.length, isFirstLoadDone, loadMore]);

  const setSentinel = useCallback(
    (node) => {
      sentinelRef.current = node;

      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node) return;
      if (!enabled) return;

      observerRef.current = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];

          if (!firstEntry?.isIntersecting) return;
          if (isFetchingRef.current) return;
          if (!hasMore) return;

          loadMore();
        },
        {
          root,
          rootMargin,
          threshold,
        },
      );

      observerRef.current.observe(node);
    },
    [enabled, hasMore, loadMore, root, rootMargin, threshold],
  );

  return {
    items,
    hasMore,
    nextPage,
    isLoading,
    isFirstLoadDone,
    error,
    sentinelRef: setSentinel,
    loadMore,
    reload,
    reset,
    hydrate,
  };
}
